import { useCallback, useEffect, useState } from 'react'

import { api, ApiError, type Match, type Schedule } from '../lib/api'
import { usePush } from '../lib/usePush'

type Props = { onPlay: (link: string) => void }

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  timeZone: 'Europe/Madrid',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
}

function formatKickoff(iso: string): string {
  return new Date(iso).toLocaleString('ca-ES', DATE_FORMAT)
}

/** Day only: the published time is a placeholder until the league fixes it. */
function formatKickoffDay(iso: string): string {
  return new Date(iso).toLocaleDateString('ca-ES', {
    timeZone: 'Europe/Madrid',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

/** How old the cached fixtures are, so stale data cannot pass for current. */
function staleness(lastRefresh: number): string {
  const hours = (Date.now() - lastRefresh) / 3_600_000
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`
  if (hours < 48) return `${hours.toFixed(1)} h`
  return `${Math.round(hours / 24)} dies`
}

function MatchRow({
  match,
  options,
  onPlay,
  onSaved,
}: {
  match: Match
  options: string[]
  onPlay: (link: string) => void
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(match.link ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(link: string | null) {
    setBusy(true)
    setError(null)
    try {
      await api.setMatchLink(match.id, link)
      setEditing(false)
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className={`match${match.state === 'in' ? ' match--live' : ''}`}>
      <div className="match__when">
        <span className="match__time">
          {match.timeConfirmed
            ? formatKickoff(match.kickoff)
            : `${formatKickoffDay(match.kickoff)} · hora per confirmar`}
        </span>
        <span className="match__comp">{match.competition}</span>
      </div>

      <div className="match__teams">
        {match.atHome ? 'vs ' : 'a '}
        <strong>{match.opponent}</strong>
        {match.platform && (
          <span
            className={`match__tv match__tv--${match.platformSource ?? 'none'}`}
            title={
              match.platformSource === 'manual'
                ? 'Triat per tu per a aquest partit'
                : match.platformSource === 'auto'
                  ? 'Publicat al calendari de Marca'
                  : 'Valor per defecte de la competició'
            }
          >
            {match.platform}
          </span>
        )}
        <select
          className="match__platform"
          value={match.platformSource === 'manual' ? match.platform ?? '' : ''}
          onChange={(e) => void api.setMatchPlatform(match.id, e.target.value || null).then(onSaved)}
          title="Deixa-ho automàtic o força una plataforma"
        >
          <option value="">{match.platform ? 'Automàtic' : 'On es veu?'}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {match.state === 'in' && <span className="match__badge">en joc</span>}
        {match.venue && <span className="match__venue">{match.venue}</span>}
      </div>

      <div className="match__actions">
        {match.link && !editing && (
          <>
            <button className="btn btn--primary btn--small" onClick={() => onPlay(match.link!)}>
              Reprodueix
            </button>
            <button className="btn btn--small" onClick={() => setEditing(true)}>
              Canvia
            </button>
            <button className="btn btn--small" disabled={busy} onClick={() => void save(null)}>
              Treu
            </button>
          </>
        )}

        {!match.link && !editing && (
          <button className="btn btn--small" onClick={() => setEditing(true)}>
            Afegeix enllaç
          </button>
        )}

        {editing && (
          <div className="match__edit">
            <input
              className="linkform__input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="acestream://… o el hash"
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="off"
              inputMode="url"
              autoFocus
            />
            <button className="btn btn--primary btn--small" disabled={busy || !draft.trim()} onClick={() => void save(draft.trim())}>
              Desa
            </button>
            <button className="btn btn--small" onClick={() => { setEditing(false); setDraft(match.link ?? '') }}>
              Cancel·la
            </button>
          </div>
        )}
      </div>

      {error && <p className="panel__error">{error}</p>}
    </li>
  )
}

export function SchedulePanel({ onPlay }: Props) {
  const [schedule, setSchedule] = useState<Schedule | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { phase, error: pushError, enable, disable, swStale } = usePush()

  const load = useCallback(async () => {
    try {
      setSchedule(await api.getSchedule())
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function refresh() {
    setBusy(true)
    try {
      await api.refreshSchedule()
      await load()
    } finally {
      setBusy(false)
    }
  }

  // Finished matches are noise here; the point of the panel is what is coming.
  const upcoming = (schedule?.matches ?? []).filter((m) => m.state !== 'post')

  return (
    <section className="panel">
      <header className="panel__header">
        <h2>Calendari{schedule ? ` · ${schedule.team}` : ''}</h2>
        <button className="btn btn--ghost" onClick={() => void refresh()} disabled={busy}>
          {busy ? 'Actualitzant…' : 'Actualitza'}
        </button>
      </header>

      <div className="notify">
        {phase === 'unsupported' && <span className="panel__hint">Aquest navegador no admet notificacions.</span>}
        {phase === 'denied' && (
          <span className="panel__hint">Notificacions bloquejades al navegador; canvia-ho als permisos del lloc.</span>
        )}
        {phase === 'busy' && <span className="panel__hint">…</span>}
        {(phase === 'off' || phase === 'on') && (
          <label className="toggle">
            <input
              type="checkbox"
              checked={phase === 'on'}
              onChange={() => void (phase === 'on' ? disable() : enable())}
            />
            Notifica'm abans del partit
          </label>
        )}

      </div>
      {swStale && (
        <p className="panel__error">
          El service worker actiu és antic i no sap rebre notificacions. Tanca totes les pestanyes
          d'AceCast (i l'app, si la tens instal·lada) i torna a obrir-la.
        </p>
      )}
      {pushError && <p className="panel__error">{pushError}</p>}

      {error && <p className="panel__error">{error}</p>}

      {schedule && schedule.lastError && (
        <p className="panel__error">
          El calendari no s'ha pogut actualitzar
          {schedule.lastRefresh > 0 && ` des de fa ${staleness(schedule.lastRefresh)}`}, o sigui que
          el que veus pot estar desfasat. Motiu: {schedule.lastError}
        </p>
      )}

      {upcoming.length === 0 ? (
        <p className="panel__hint">{schedule ? 'Cap partit programat.' : 'Carregant…'}</p>
      ) : (
        <ul className="matches">
          {upcoming.map((match) => (
            <MatchRow
              key={match.id}
              match={match}
              options={schedule?.platformOptions ?? []}
              onPlay={onPlay}
              onSaved={() => void load()}
            />
          ))}
        </ul>
      )}
    </section>
  )
}
